/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { TROY_SUPPORT_LANG } from "common/constants/misc"
import {
  getLocaleFromBrowserLanguage,
  normalizeTroyLangLocale,
} from "common/utils/locale"
import { DisabledEditItem, DisabledEditModifierGroup } from "types/models/menu"
import { BaseStorageHelper } from "common/utils/storage"
import { FeatureFlag } from "constants/features"
import {
  TROY_HELP_CENTER_LINKS_V2,
  TROY_ORDER_MANAGER_HELP_CENTER_LINKS,
  TROY_ORDER_MANAGER_HELP_CENTER_LINKS_STAGING,
} from "constants/links"
import { uniq, isEqual, get } from "lodash"
import dayjs from "dayjs"
import { UserProfileInfo } from "types/models/user_profile.type"
import { isPrd } from "constants/domain"
import {
  OrderManagerStorageConfig,
  OrderManagerNotificationConfig,
} from "order-manager/types/models/storage_config"

class StorageHelper extends BaseStorageHelper {
  private static USER_PROFILE_KEY = "userprofileInfo"

  private static BUSINESS_LINE_IDS_LS = "businessLineIds"

  private static DISABLED_ITEMS_KEY = "disabledItems"

  private static DISABLED_MODIFIER_GROUPS_KEY = "disabledModifierGroups"

  private static MENU_SEARCH_HISTORY_KEY = "menuSearchHistory"

  private static STORE_SEARCH_HISTORY_KEY = "storeSearchHistory"

  private static ORDER_SEARCH_HISTORY_KEY = "orderSearchHistory"

  private static MODIFIERGROUP_SEARCH_HISTORY_KEY = "modifierGroupSearchHistory"

  private static FEATURE_FLAGS_KEY = "featureFlags"

  private static LANG_KEY = "lang"

  private static SHOW_MENU_GROUP_BANNER = "showMenuGroupBanner"

  private static TOGGLE_FEATURE = "__TOGGLE_FEATURE__"

  private static ORDER_MANAGER_CONFIG = "orderManagerConfig"

  private static ORDER_MANAGER_NOTIFICATION_CONFIG =
    "orderManagerNotificationConfig"

  private static RECENT_LIMIT = 4

  private static MENU_BULKUPLOAD_ONBOARDED = "menuBulkUploadOnboarded"

  private static ALCOHOL_TAGGING_TOOLTIP_SHOWED = "alcoholTaggingTooltipShowed"

  private static TOBACCO_TAGGING_TOOLTIP_SHOWED = "tobaccoTaggingTooltipShowed"

  private static MERCHANT_PORTAL_FEATURE_FLAG = "merchantPortalFeatureFlag"

  constructor() {
    super("JWT")
  }

  public getParentEntityCountry(): string | null {
    return this.getUserProfile()?.user_profile?.parent_entity_country ?? null
  }

  public getUserCountry(): string | null {
    return this.getUserProfile()?.country ?? null
  }

  public getUserCountryID(): number {
    const country = this.getUserProfile()?.country ?? ""
    const mapCountry: {
      [key: string]: number
    } = {
      MY: 1,
      PH: 2,
      TH: 3,
      SG: 4,
      VN: 5,
      ID: 6,
      MM: 9,
      KH: 10,
    }
    return mapCountry[country]
  }

  public getAnalyticsID(): string {
    return this.getUserProfile()?.analytics_id ?? ""
  }

  public getEmail(): string {
    return this.getUserProfile()?.user_profile?.username ?? ""
  }

  public getLangFromLS(): string | null {
    return this.getItem(StorageHelper.LANG_KEY)
  }

  public getMerchantGrabId(): string {
    return this.getUserProfile()?.merchant_grab_id ?? ""
  }

  public getLang(): string {
    /**
     * 1. check the record in localstorage
     * 2. check browser language
     * 3. check country language
     * 4. default 'en'
     */
    let userLocale = this.getItem(StorageHelper.LANG_KEY)
    if (userLocale) {
      const mapLocale: {
        [key: string]: string
      } = {
        GB: "en",
        EN: "en",
        ID: "id",
        VN: "vi",
        CN: "zh",
        TH: "th",
        MY: "ms",
      }
      return normalizeTroyLangLocale(
        mapLocale[userLocale.toLocaleUpperCase()] ?? userLocale,
      )
    }
    const availableLocales = TROY_SUPPORT_LANG
    userLocale = getLocaleFromBrowserLanguage({
      availableLocales,
      defaultLocale: "",
      navigator: window.navigator,
    })
    if (userLocale) {
      return userLocale
    }
    const userCountry = this.getUserCountry() || ""
    const countryLocaleMapping: {
      [idx: string]: string
    } = {
      ID: "id",
      SG: "en",
    }
    userLocale = countryLocaleMapping[userCountry] || "en"
    return userLocale
  }

  public setLang(locale: string) {
    this.setItem(StorageHelper.LANG_KEY, locale)
  }

  public getMenuSearchHistory(merchantID: string) {
    let localData: {
      [itemID: string]: string[]
    } = {}
    try {
      localData = JSON.parse(
        this.getItem(StorageHelper.MENU_SEARCH_HISTORY_KEY) ?? "{}",
      )
    } catch (e) {
      // dont care
    }

    return uniq(localData[merchantID] ?? [])
  }

  public setMenuSearchHistory({
    merchantID,
    itemIDs,
  }: {
    merchantID: string
    itemIDs: string[]
  }) {
    let localData: {
      [itemID: string]: string[]
    } = {}
    try {
      localData = JSON.parse(
        this.getItem(StorageHelper.MENU_SEARCH_HISTORY_KEY) ?? "{}",
      )
    } catch (e) {
      // dont care
    }
    const newLocalData = {
      ...localData,
      [merchantID]: uniq(itemIDs),
    }

    this.setItem(
      StorageHelper.MENU_SEARCH_HISTORY_KEY,
      JSON.stringify(newLocalData),
    )
  }

  public getOrderSearchHistory(merchantID: string) {
    let localData: {
      [itemID: string]: string[]
    } = {}
    try {
      localData = JSON.parse(
        this.getItem(StorageHelper.ORDER_SEARCH_HISTORY_KEY) ?? "{}",
      )
    } catch (e) {
      // dont care
    }

    return uniq(localData[merchantID] ?? [])
  }

  public setOrderSearchHistory({
    merchantID,
    displayID,
  }: {
    merchantID: string
    displayID: string[]
  }) {
    let localData: {
      [itemID: string]: string[]
    } = {}
    try {
      localData = JSON.parse(
        this.getItem(StorageHelper.ORDER_SEARCH_HISTORY_KEY) ?? "{}",
      )
    } catch (e) {
      // dont care
    }
    const newLocalData = {
      ...localData,
      [merchantID]: uniq(displayID),
    }

    this.setItem(
      StorageHelper.MENU_SEARCH_HISTORY_KEY,
      JSON.stringify(newLocalData),
    )
  }

  public getModifierGroupSearchHistory(merchantID: string) {
    let localData: {
      [itemID: string]: string[]
    } = {}
    try {
      localData = JSON.parse(
        this.getItem(StorageHelper.MODIFIERGROUP_SEARCH_HISTORY_KEY) ?? "{}",
      )
    } catch (e) {
      // dont care
    }

    return uniq(localData[merchantID] ?? [])
  }

  public setModifierGroupSearchHistory({
    merchantID,
    modifierGroupIDs,
  }: {
    merchantID: string
    modifierGroupIDs: string[]
  }) {
    let localData: {
      [itemID: string]: string[]
    } = {}
    try {
      localData = JSON.parse(
        this.getItem(StorageHelper.MODIFIERGROUP_SEARCH_HISTORY_KEY) ?? "{}",
      )
    } catch (e) {
      // dont care
    }
    const newLocalData = {
      ...localData,
      [merchantID]: uniq(modifierGroupIDs),
    }

    this.setItem(
      StorageHelper.MODIFIERGROUP_SEARCH_HISTORY_KEY,
      JSON.stringify(newLocalData),
    )
  }

  public getDisabledItem(itemID: string): DisabledEditItem | undefined {
    try {
      const localData: {
        [itemID: string]: DisabledEditItem
      } = JSON.parse(this.getItem(StorageHelper.DISABLED_ITEMS_KEY) ?? "{}")

      return localData?.[itemID ?? ""]
    } catch (e) {
      return undefined
    }
  }

  public isItemEditDisabled(itemID: string): boolean {
    const itemDate = this.getDisabledItem(itemID)?.date
    if (itemDate && dayjs() < dayjs(itemDate, "DD/MM/YY")) {
      return true
    }
    return false
  }

  public setItemEditDisabled(data: DisabledEditItem) {
    let localData: {
      [itemID: string]: string
    } = {}
    try {
      localData = JSON.parse(
        this.getItem(StorageHelper.DISABLED_ITEMS_KEY) ?? "{}",
      )
    } catch (e) {
      // dont care
    }
    const newLocalData = {
      ...localData,
      [data.itemID]: data,
    }

    this.setItem(StorageHelper.DISABLED_ITEMS_KEY, JSON.stringify(newLocalData))
  }

  public setModifierGroupDisableEdit(modifierGroup: DisabledEditModifierGroup) {
    try {
      const modifierGroups = JSON.parse(
        this.getItem(StorageHelper.DISABLED_MODIFIER_GROUPS_KEY) || "[]",
      ) as DisabledEditModifierGroup[]
      const isModifierGroupExist = modifierGroups?.find(
        mg => mg.modifierGroupID === modifierGroup.modifierGroupID,
      )

      if (
        !isModifierGroupExist &&
        dayjs(modifierGroup.date, "DD/MM/YY") > dayjs()
      ) {
        modifierGroups?.push(modifierGroup)
        this.setItem(
          StorageHelper.DISABLED_MODIFIER_GROUPS_KEY,
          JSON.stringify(modifierGroups),
        )
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  public setOrderManagerConfig(orderManagerConfig: OrderManagerStorageConfig) {
    try {
      this.setItem(
        StorageHelper.ORDER_MANAGER_CONFIG,
        JSON.stringify(orderManagerConfig),
      )
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  public setOrderManagerNotificationConfig(
    orderManagerNotificationConfig: OrderManagerNotificationConfig,
  ) {
    try {
      this.setItem(
        StorageHelper.ORDER_MANAGER_NOTIFICATION_CONFIG,
        JSON.stringify(orderManagerNotificationConfig),
      )
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  public isModifierGroupEditDisabled(modifierGroupID: string): boolean {
    try {
      let modifierGroups = JSON.parse(
        this.getItem(StorageHelper.DISABLED_MODIFIER_GROUPS_KEY) || "[]",
      ) as DisabledEditModifierGroup[]
      const modifierGroup = modifierGroups?.find(
        mg => mg.modifierGroupID === modifierGroupID,
      )
      if (modifierGroup && dayjs() < dayjs(modifierGroup.date, "DD/MM/YY")) {
        return true
      }

      modifierGroups = modifierGroups?.filter(
        mg => mg.modifierGroupID !== modifierGroup?.modifierGroupID,
      )
      this.removeItem(StorageHelper.DISABLED_MODIFIER_GROUPS_KEY)
      this.setItem(
        StorageHelper.DISABLED_MODIFIER_GROUPS_KEY,
        JSON.stringify(modifierGroups),
      )
      return false
    } catch (e) {
      return false
    }
  }

  public getDisabledModifierGroup(
    modifierGroupID: string,
  ): DisabledEditModifierGroup | undefined {
    try {
      const modifierGroups = JSON.parse(
        this.getItem(StorageHelper.DISABLED_MODIFIER_GROUPS_KEY) || "[]",
      ) as DisabledEditModifierGroup[]
      return modifierGroups?.find(mg => mg.modifierGroupID === modifierGroupID)
    } catch (e) {
      return undefined
    }
  }

  public setStoreSearchHistory(record: any) {
    let localData
    try {
      localData = JSON.parse(
        this.getItem(StorageHelper.STORE_SEARCH_HISTORY_KEY) ?? "[]",
      )
    } catch (e) {
      // don't care
    }

    const itemExist = localData?.find((lo: any) => isEqual(lo, record))

    if (!itemExist) {
      if (localData?.length + 1 > StorageHelper.RECENT_LIMIT) {
        localData = localData.slice(0, -1)
      }
      localData = [record, ...localData]
      this.setItem(
        StorageHelper.STORE_SEARCH_HISTORY_KEY,
        JSON.stringify(localData),
      )
    }
  }

  public getStoreSearchHistory(): any {
    let result
    try {
      result = JSON.parse(
        this.getItem(StorageHelper.STORE_SEARCH_HISTORY_KEY) ?? "[]",
      )
      return result
    } catch (e) {
      // don't care
      return []
    }
  }

  public clearStoreSearchHistory(): void {
    // only remove store recent search
    // modifier group recent search would be persisted
    let result
    try {
      result = JSON.parse(
        this.getItem(StorageHelper.STORE_SEARCH_HISTORY_KEY) ?? "[]",
      )
      result = result?.filter((r: any) => r?.additionalMerchantID)
      this.setItem(
        StorageHelper.STORE_SEARCH_HISTORY_KEY,
        JSON.stringify(result),
      )
    } catch (e) {
      // don't care
    }
  }

  public getUserCurrency(): string {
    try {
      const userProfile = get(
        JSON.parse(this.getItem("userprofileInfo") ?? "{}"),
        "user_profile",
      )
      const country = get(userProfile, "parent_entity_country")
      switch (country.toUpperCase()) {
        case "SG":
          return "S$"
        case "MY":
          return "RM"
        case "ID":
          return "Rp"
        case "TH":
          return "฿"
        case "VN":
          return "₫"
        case "PH":
          return "₱"
        case "KH":
          return "៛"
        default:
          return ""
      }
    } catch (err) {
      return ""
    }
  }

  public getTroyHelpCenterUrlV2(): string {
    try {
      const userProfile = get(
        JSON.parse(this.getItem("userprofileInfo") ?? "{}"),
        "user_profile",
      )
      const country = get(userProfile, "parent_entity_country")
      // @ts-ignore
      return TROY_HELP_CENTER_LINKS_V2[country]
    } catch (err) {
      return ""
    }
  }

  public getTroyOrderManagerHelpCenterUrl(): string {
    try {
      const userProfile = get(
        JSON.parse(this.getItem("userprofileInfo") ?? "{}"),
        "user_profile",
      )
      const country = get(userProfile, "parent_entity_country")
      return isPrd() // @ts-ignore
        ? TROY_ORDER_MANAGER_HELP_CENTER_LINKS[country] // @ts-ignore
        : TROY_ORDER_MANAGER_HELP_CENTER_LINKS_STAGING[country]
    } catch (err) {
      return ""
    }
  }

  getUserProfile(): UserProfileInfo | null {
    try {
      const userProfileInfo = JSON.parse(
        this.getItem(StorageHelper.USER_PROFILE_KEY) || "{}",
      ) as UserProfileInfo
      return userProfileInfo
    } catch (e) {
      return null
    }
  }

  getFeatureFlags(): { [key: string]: boolean } {
    try {
      const featureFlags = JSON.parse(
        this.getItem(StorageHelper.FEATURE_FLAGS_KEY) || "{}",
      )
      return featureFlags
    } catch (err) {
      return {}
    }
  }

  getToggleFeature(): { [key: string]: boolean } {
    try {
      const toggleFeature = JSON.parse(
        this.getItem(StorageHelper.TOGGLE_FEATURE) || "{}",
      )
      return toggleFeature
    } catch (err) {
      return {}
    }
  }

  public getOrderManagerConfig(): OrderManagerStorageConfig | null {
    try {
      const orderManagerConfig = JSON.parse(
        this.getItem(StorageHelper.ORDER_MANAGER_CONFIG) || "{}",
      ) as OrderManagerStorageConfig

      return orderManagerConfig
    } catch (err) {
      return null
    }
  }

  public getOrderManagerNotificationConfig(): OrderManagerNotificationConfig | null {
    try {
      const orderManagerNotificationConfig = JSON.parse(
        this.getItem(StorageHelper.ORDER_MANAGER_NOTIFICATION_CONFIG) || "{}",
      ) as OrderManagerNotificationConfig

      return orderManagerNotificationConfig
    } catch (err) {
      return null
    }
  }

  isFeatureEnabled(featureFlag: FeatureFlag) {
    return this.getFeatureFlags()?.[featureFlag] ?? false
  }

  getShowMenuGroupBanner(): boolean | null {
    const showMenuGroupBanner = this.getItem(
      StorageHelper.SHOW_MENU_GROUP_BANNER,
    )
    if (showMenuGroupBanner) {
      return showMenuGroupBanner === "true"
    }
    return null
  }

  setShowMenuGroupBanner(showMenuGroupBanner: boolean): void {
    this.setItem(
      StorageHelper.SHOW_MENU_GROUP_BANNER,
      showMenuGroupBanner ? "true" : "false",
    )
  }

  isMenuBulkUploadOnboarded(): boolean {
    return this.getItem(StorageHelper.MENU_BULKUPLOAD_ONBOARDED) === "true"
  }

  setMenuBulkUploadOnboarded(): void {
    this.setItem(StorageHelper.MENU_BULKUPLOAD_ONBOARDED, "true")
  }

  setAlcoholTaggingTooltipShowed(): void {
    this.setItem(StorageHelper.ALCOHOL_TAGGING_TOOLTIP_SHOWED, "true")
  }

  alcoholTaggingTooltipShowed(): boolean {
    return this.getItem(StorageHelper.ALCOHOL_TAGGING_TOOLTIP_SHOWED) === "true"
  }

  setTobaccoTaggingTooltipShowed(): void {
    this.setItem(StorageHelper.TOBACCO_TAGGING_TOOLTIP_SHOWED, "true")
  }

  tobaccoTaggingTooltipShowed(): boolean {
    return this.getItem(StorageHelper.TOBACCO_TAGGING_TOOLTIP_SHOWED) === "true"
  }

  public getMerchantPortalFeatureFlag<T>(key: string): T {
    const result = JSON.parse(
      this.getItem(StorageHelper.MERCHANT_PORTAL_FEATURE_FLAG) || "{}",
    )
    return result[key]
  }

  public setMerchantPortalFeatureFlag(featureFlags: Record<string, any>): void {
    this.setItem(
      StorageHelper.MERCHANT_PORTAL_FEATURE_FLAG,
      JSON.stringify(featureFlags),
    )
  }

  public isCookieAuthEnabled(): boolean {
    // This is to check for any cases where this page might be loaded with an injected JWT token
    // through a WebView flow. In this case, we need to fallback to the old JWT token authn flow
    // to avoid the cookie authn flow from failing. Under normal circumstances, cookie authn would
    // not have JWT token. This check is still needed even after the cookie authn rollout is done.
    const jwtToken = this.getToken()

    return !jwtToken
  }

  /**
   * START DEV ONLY - Login Flow Enhancement
   */

  public setUserProfileInfo(userProfile: UserProfileInfo): void {
    this.setItem(StorageHelper.USER_PROFILE_KEY, JSON.stringify(userProfile))
  }

  public setBusinessLineIds(businessLineIds: string[]): void {
    this.setItem(
      StorageHelper.BUSINESS_LINE_IDS_LS,
      JSON.stringify(businessLineIds),
    )
  }

  public getBusinessLineIds(): string[] {
    return JSON.parse(this.getItem(StorageHelper.BUSINESS_LINE_IDS_LS) || "[]")
  }

  public setMexPortalFeatureFlags(featureFlag: Record<string, any>): void {
    this.setItem(StorageHelper.FEATURE_FLAGS_KEY, JSON.stringify(featureFlag))
  }

  public getMexPortalFeatureFlags(featureFlag: string): Record<string, any> {
    return JSON.parse(this.getItem(StorageHelper.FEATURE_FLAGS_KEY) || "{}")[
      featureFlag
    ]
  }

  /**
   * END DEV ONLY - Login Flow Enhancement
   */
}

export const storageHelper = new StorageHelper()
